import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../../lib/adminApi';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { Plus, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import { Stars } from '../Testimonials';
import TestimonialDialog from './TestimonialDialog';


export const TestimonialsManager = ({ onAuthError }) => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get(`/testimonials/all`);
      setItems(data.data);
    } catch (err) { onAuthError(err); }
  }, [onAuthError]);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (form.id) await adminApi.put(`/testimonials/${form.id}`, form);
      else await adminApi.post(`/testimonials`, form);
      toast({ title: form.id ? 'Testimonio actualizado' : 'Testimonio creado' });
      setEditing(null);
      load();
    } catch (err) {
      if (!onAuthError(err)) toast({ title: 'Error', description: 'No se pudo guardar el testimonio.', variant: 'destructive' });
    }
  };

  const toggleActive = (t) => save({ ...t, activo: !t.activo });

  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar el testimonio de ${t.nombre}?`)) return;
    try {
      await adminApi.delete(`/testimonials/${t.id}`);
      toast({ title: 'Testimonio eliminado' });
      load();
    } catch (err) {
      if (!onAuthError(err)) toast({ title: 'Error', description: 'No se pudo eliminar.', variant: 'destructive' });
    }
  };

  return (
    <section className="bg-white/5 border border-white/10 rounded-3xl p-6" data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Testimonios de clientes</h2>
          <p className="text-slate-400 text-sm">Se muestran en la página antes del formulario. Puedes ocultarlos, editarlos o agregar nuevos.</p>
        </div>
        <Button data-testid="testimonial-add-btn" onClick={() => setEditing({ nombre: '', ciudad: '', texto: '', estrellas: 5, foto_url: '', activo: true })} className="rounded-xl bg-blue-600 hover:bg-blue-500">
          <Plus className="h-4 w-4 mr-2" /> Nuevo
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {items.map((t) => (
          <div key={t.id} data-testid="testimonial-admin-card" className={`rounded-2xl border p-4 flex flex-col gap-3 ${t.activo ? 'border-white/10 bg-slate-900/50' : 'border-dashed border-white/10 bg-transparent opacity-60'}`}>
            <div className="flex items-center gap-3">
              {t.foto_url
                ? <img src={t.foto_url} alt={t.nombre} className="h-12 w-12 rounded-full object-cover" />
                : <div className="h-12 w-12 rounded-full bg-blue-600/40 flex items-center justify-center font-bold">{t.nombre.charAt(0)}</div>}
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{t.nombre}</p>
                <p className="text-xs text-slate-400 truncate">{t.ciudad}</p>
              </div>
              <Badge className={`border-0 ${t.activo ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-300'}`}>{t.activo ? 'Visible' : 'Oculto'}</Badge>
            </div>
            <Stars count={t.estrellas} />
            <p className="text-sm text-slate-300 line-clamp-3 flex-1">{t.texto}</p>
            <div className="flex gap-2 pt-1">
              <Button data-testid="testimonial-edit-btn" size="sm" variant="outline" onClick={() => setEditing(t)} className="flex-1 rounded-lg border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"><Pencil className="h-3.5 w-3.5 mr-1" /> Editar</Button>
              <Button data-testid="testimonial-toggle-btn" size="sm" variant="outline" onClick={() => toggleActive(t)} className="rounded-lg border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white" title={t.activo ? 'Ocultar' : 'Mostrar'}>
                {t.activo ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
              <Button data-testid="testimonial-delete-btn" size="sm" variant="outline" onClick={() => remove(t)} className="rounded-lg border-red-500/30 bg-transparent text-red-300 hover:bg-red-500/10 hover:text-red-200"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-slate-500 text-sm col-span-3">Aún no hay testimonios. Agrega el primero.</p>}
      </div>

      <TestimonialDialog testimonial={editing} onClose={() => setEditing(null)} onSave={save} />
    </section>
  );
};

export default TestimonialsManager;
